import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Badge,
  Tooltip,
  Snackbar,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  Visibility,
  CalendarToday,
  Event,
  Schedule,
  History,
  Download,
  Person,
  Phone,
  Email,
  Room,
  Payment,
  LocalOffer,
  Discount,
  CheckCircle,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import Sidebar from '../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../components/TopNav_hotelAdmin';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const HotelBookingsManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    date: new Date(),
    startDate: null,
    endDate: null,
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // Navigation and sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  // Snackbar function
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/Entri');
  };

  const handleNotificationClick = () => {
    alert('You have new notifications!');
  };

  // Get hotel code from user context
  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      const hotelCode = storedProfile.hotelcode || 
                       storedProfile.hotel_code || 
                       storedProfile.hotelCode || 
                       'eHT5678042024';
      
      if (!hotelCode) {
        console.error('No hotel code found!');
        return 'eHT5678042024';
      }
      
      return hotelCode;
    } catch (error) {
      console.error('Error getting hotel code:', error);
      return 'eHT5678042024';
    }
  };

  // API service functions
  const api = {
    getBookings: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE_URL}/hotel/admin/booking-details.php`, {
          params: { 
            hotel_code: getHotelCode(), 
            ...params 
          },
          timeout: 10000,
        });
        
        return response.data;
      } catch (error) {
        console.error('Error fetching bookings:', error);
        return { 
          success: false, 
          error: error.response?.data?.error || error.message || 'Failed to fetch bookings', 
          bookings: [] 
        };
      }
    },
  };

  // Load bookings based on active tab
  const loadBookings = async () => {
    setLoading(true);
    setError('');
    
    try {
      let params = {};
      const today = new Date().toISOString().split('T')[0];
      
      switch (activeTab) {
        case 0: // Today's Bookings
          params = {
            filter_type: 'today',
            date: today
          };
          break;
        case 1: // Past Bookings
          params = {
            filter_type: 'past',
            date: today
          };
          break;
        case 2: // Future Bookings
          params = {
            filter_type: 'future',
            date: today
          };
          break;
        case 3: // Custom Date Range
          if (filters.startDate && filters.endDate) {
            params = {
              filter_type: 'custom',
              start_date: filters.startDate.toISOString().split('T')[0],
              end_date: filters.endDate.toISOString().split('T')[0]
            };
          } else {
            params = { filter_type: 'today', date: today };
          }
          break;
        default:
          params = { filter_type: 'today', date: today };
      }
      
      const bookingsData = await api.getBookings(params);
      
      if (bookingsData.success) {
        setBookings(bookingsData.bookings || []);
        showSnackbar(`Loaded ${bookingsData.bookings?.length || 0} bookings`, 'success');
      } else {
        setError(bookingsData.error || 'Failed to load bookings');
        setBookings([]);
        showSnackbar(bookingsData.error || 'Failed to load bookings', 'error');
      }
    } catch (error) {
      const errorMsg = `Network error: ${error.message}`;
      setError(errorMsg);
      setBookings([]);
      showSnackbar('Network error loading bookings', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load bookings when tab changes or date filters change
  useEffect(() => {
    loadBookings();
  }, [activeTab]);

  // Apply search and status filters
  useEffect(() => {
    applyFilters();
  }, [filters, bookings]);

  const applyFilters = () => {
    let filtered = [...bookings];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(booking => 
        (booking.guest_name?.toLowerCase() || '').includes(searchTerm) ||
        (booking.booking_number?.toLowerCase() || '').includes(searchTerm) ||
        (booking.guest_email?.toLowerCase() || '').includes(searchTerm) ||
        (booking.room_name?.toLowerCase() || '').includes(searchTerm)
      );
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(booking => 
        booking.booking_status === filters.status
      );
    }

    setFilteredBookings(filtered);
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleDateChange = (field) => (newValue) => {
    setFilters(prev => ({
      ...prev,
      [field]: newValue
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      date: new Date(),
      startDate: null,
      endDate: null,
    });
    showSnackbar('Filters cleared', 'info');
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsDialogOpen(true);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Calculate booking statistics
  const calculateStats = () => {
    const today = new Date().toISOString().split('T')[0];
    const todayBookings = bookings.filter(b => b.check_in_date === today);
    const checkedIn = bookings.filter(b => b.booking_status === 'checked_in').length;
    const pending = bookings.filter(b => b.booking_status === 'pending').length;
    const confirmed = bookings.filter(b => b.booking_status === 'confirmed').length;
    const totalDiscount = bookings.reduce((sum, b) => sum + parseFloat(b.discount_amount || 0), 0);
    const discountedBookings = bookings.filter(b => parseFloat(b.discount_amount || 0) > 0).length;

    return {
      totalBookings: filteredBookings.length,
      todayBookings: todayBookings.length,
      checkedIn,
      pending,
      confirmed,
      totalDiscount,
      discountedBookings
    };
  };

  const stats = calculateStats();

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'checked_in': return 'info';
      case 'checked_out': return 'secondary';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  // Get payment status color
  const getPaymentColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'partial': return 'warning';
      case 'pending': return 'error';
      default: return 'default';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₦0';
    return `₦${parseFloat(amount).toLocaleString('en-NG')}`;
  };

  // Generate avatar color based on name
  const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  const tabLabels = [
    { label: "Today's Bookings", icon: <CalendarToday /> },
    { label: 'Past Bookings', icon: <History /> },
    { label: 'Future Bookings', icon: <Schedule /> },
    { label: 'Custom Range', icon: <Event /> }
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        {/* Top Navigation */}
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        {/* Main Content */}
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Bookings Management
                </Typography>
                <Typography variant="subtitle1">
                  Manage and monitor all hotel bookings and reservations
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={() => {/* Export functionality */}}
                  sx={{ backgroundColor: 'white', color: 'primary.main', '&:hover': { backgroundColor: 'grey.100' } }}
                >
                  Export
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setFilterDialogOpen(true)}
                  sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  Filters
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadBookings}
                  disabled={loading}
                  sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Total Bookings
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {stats.totalBookings}
                      </Typography>
                    </Box>
                    <CalendarToday sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Today's Bookings
                      </Typography>
                      <Typography variant="h4" color="info.main">
                        {stats.todayBookings}
                      </Typography>
                    </Box>
                    <Badge color="info" badgeContent={stats.todayBookings} sx={{ '& .MuiBadge-badge': { top: 10, right: 10 } }}>
                      <Event sx={{ fontSize: 40, color: 'info.main' }} />
                    </Badge>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Checked In
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {stats.checkedIn}
                      </Typography>
                    </Box>
                    <Person sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Discounted Bookings
                      </Typography>
                      <Typography variant="h4" color="warning.main">
                        {stats.discountedBookings}
                      </Typography>
                    </Box>
                    <Discount sx={{ fontSize: 40, color: 'warning.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Total Discount
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {formatCurrency(stats.totalDiscount)}
                      </Typography>
                    </Box>
                    <LocalOffer sx={{ fontSize: 40, color: 'error.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Paper elevation={2} sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
            >
              {tabLabels.map((tab, index) => (
                <Tab
                  key={index}
                  icon={tab.icon}
                  iconPosition="start"
                  label={isMobile ? tab.label.split(' ')[0] : tab.label}
                  sx={{ minHeight: 64 }}
                />
              ))}
            </Tabs>
          </Paper>

          {/* Custom Date Range Picker */}
          {activeTab === 3 && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Select Date Range
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <DatePicker
                      label="Start Date"
                      value={filters.startDate}
                      onChange={handleDateChange('startDate')}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <DatePicker
                      label="End Date"
                      value={filters.endDate}
                      onChange={handleDateChange('endDate')}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      variant="contained"
                      onClick={loadBookings}
                      disabled={!filters.startDate || !filters.endDate}
                      fullWidth
                    >
                      Apply
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </LocalizationProvider>
          )}

          {/* Search Bar */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search bookings by guest name, booking number, or room..."
                  value={filters.search}
                  onChange={handleFilterChange('search')}
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={handleFilterChange('status')}
                    label="Status Filter"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="checked_in">Checked In</MenuItem>
                    <MenuItem value="checked_out">Checked Out</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  fullWidth
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Bookings Table */}
          <Paper elevation={3}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Booking Details</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Guest Information</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Room Details</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Dates</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Payment</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Discount</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredBookings.length > 0 ? (
                        filteredBookings
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((booking) => {
                            const hasDiscount = parseFloat(booking.discount_amount || 0) > 0;
                            return (
                              <TableRow key={booking.id} hover>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                      {booking.booking_number}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {formatDate(booking.created_at)}
                                    </Typography>
                                    <Chip 
                                      label={booking.source} 
                                      size="small"
                                      variant="outlined"
                                      sx={{ mt: 0.5 }}
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar 
                                      sx={{ 
                                        bgcolor: stringToColor(booking.guest_name),
                                        width: 40, 
                                        height: 40 
                                      }}
                                    >
                                      {booking.guest_name?.[0]}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2" fontWeight="medium">
                                        {booking.guest_name}
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                        <Email fontSize="small" color="action" />
                                        <Typography variant="caption">{booking.guest_email}</Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Phone fontSize="small" color="action" />
                                        <Typography variant="caption">{booking.guest_phone}</Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Room color="action" />
                                    <Box>
                                      <Typography variant="body2" fontWeight="medium">
                                        {booking.room_name}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        {booking.room_number} • {booking.room_type}
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        {booking.number_of_guests} guest(s)
                                      </Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2">
                                      <strong>Check-in:</strong> {formatDate(booking.check_in_date)}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Check-out:</strong> {formatDate(booking.check_out_date)}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {Math.ceil((new Date(booking.check_out_date) - new Date(booking.check_in_date)) / (1000 * 60 * 60 * 24))} night(s)
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                      {formatCurrency(booking.total_amount)}
                                    </Typography>
                                    <Chip 
                                      label={booking.payment_status} 
                                      color={getPaymentColor(booking.payment_status)}
                                      size="small"
                                      sx={{ mt: 0.5 }}
                                    />
                                    <Typography variant="caption" display="block" color="textSecondary">
                                      {booking.payment_method}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {hasDiscount ? (
                                    <Box>
                                      <Chip 
                                        icon={<LocalOffer />}
                                        label={`${booking.discount_percentage}% OFF`}
                                        color="warning"
                                        size="small"
                                        sx={{ mb: 0.5 }}
                                      />
                                      <Typography variant="caption" display="block" color="error.main">
                                        Saved: {formatCurrency(booking.discount_amount)}
                                      </Typography>
                                      {booking.discount_reason && (
                                        <Tooltip title={booking.discount_reason}>
                                          <Typography variant="caption" display="block" color="textSecondary" noWrap sx={{ maxWidth: 120 }}>
                                            {booking.discount_reason}
                                          </Typography>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  ) : (
                                    <Chip 
                                      label="No Discount"
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={booking.booking_status} 
                                    color={getStatusColor(booking.booking_status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      color="primary"
                                      onClick={() => handleViewDetails(booking)}
                                    >
                                      <Visibility />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" color="textSecondary">
                              No bookings found
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                              {Object.values(filters).some(f => f) 
                                ? 'Try adjusting your filters' 
                                : 'No bookings available for the selected period'
                              }
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredBookings.length > 0 && (
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredBookings.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                  />
                )}
              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Booking Details Dialog with Enhanced Discount Section */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Booking Details</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBooking && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="primary">
                  Booking Information
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Booking Number</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedBooking.booking_number}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                    <Chip 
                      label={selectedBooking.booking_status} 
                      color={getStatusColor(selectedBooking.booking_status)}
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Source</Typography>
                    <Typography variant="body1">{selectedBooking.source}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Dates</Typography>
                    <Typography variant="body1">
                      {formatDate(selectedBooking.check_in_date)} - {formatDate(selectedBooking.check_out_date)}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="primary">
                  Guest Information
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Guest Name</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedBooking.guest_name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Contact</Typography>
                    <Typography variant="body1">{selectedBooking.guest_email}</Typography>
                    <Typography variant="body1">{selectedBooking.guest_phone}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Guests</Typography>
                    <Typography variant="body1">
                      {selectedBooking.adults || 1} Adult(s), {selectedBooking.children || 0} Child(ren)
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="primary">
                  Room Information
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Room</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedBooking.room_name} ({selectedBooking.room_number})
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedBooking.room_type}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Number of Rooms</Typography>
                    <Typography variant="body1">{selectedBooking.number_of_rooms}</Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom color="primary">
                  Payment Information
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Total Amount</Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(selectedBooking.total_amount)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Paid Amount</Typography>
                    <Typography variant="body1">
                      {formatCurrency(selectedBooking.paid_amount)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Payment Status</Typography>
                    <Chip 
                      label={selectedBooking.payment_status} 
                      color={getPaymentColor(selectedBooking.payment_status)}
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Payment Method</Typography>
                    <Typography variant="body1">{selectedBooking.payment_method || 'Not specified'}</Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* Enhanced Discount Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Discount /> Discount Information
                </Typography>
                
                {parseFloat(selectedBooking.discount_amount || 0) > 0 ? (
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#fff8e7', 
                      borderLeft: `4px solid ${theme.palette.warning.main}`,
                      borderRadius: 1
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="textSecondary">Original Amount</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formatCurrency(selectedBooking.original_amount || (parseFloat(selectedBooking.total_amount) + parseFloat(selectedBooking.discount_amount)))}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="textSecondary">Discount Applied</Typography>
                        <Typography variant="body1" color="error.main" fontWeight="bold">
                          -{formatCurrency(selectedBooking.discount_amount)} ({selectedBooking.discount_percentage}% OFF)
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="textSecondary">Final Amount</Typography>
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          {formatCurrency(selectedBooking.total_amount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="textSecondary">Discount Reason</Typography>
                        <Typography variant="body1">
                          {selectedBooking.discount_reason || 'No reason provided'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="textSecondary">Approved By</Typography>
                        <Typography variant="body1">
                          {selectedBooking.discount_approved_by || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="textSecondary">Discount ID</Typography>
                        <Typography variant="body1">
                          {selectedBooking.discount_id || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                ) : (
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#f5f5f5', 
                      textAlign: 'center',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="body1" color="textSecondary">
                      No discount applied to this booking
                    </Typography>
                  </Paper>
                )}
              </Grid>

              {selectedBooking.special_requests && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom color="primary">
                    Special Requests
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body1">
                      {selectedBooking.special_requests}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelBookingsManagement;